import {
  KubernetesPodData,
  KubernetesPodData_Container,
  KubernetesPodData_ContainerState,
  KubernetesPodData_ContainerStatus,
  KubernetesPodData_EnvVar,
  KubernetesPodData_PodStatus,
} from "@/gen_src/proto/chalk/kubernetes/v1/pods_pb";
import { KubernetesNodeData } from "@/gen_src/proto/chalk/kubernetes/v1/nodes_pb";
import { KubernetesDeployment } from "@/gen_src/proto/chalk/kubernetes/v1/deployments_pb";
import { KubernetesJob } from "@/gen_src/proto/chalk/kubernetes/v1/jobs_pb";
import { KubernetesStatefulSet } from "@/gen_src/proto/chalk/kubernetes/v1/statefulsets_pb";
import { transformWindowedFeatureName } from "@/utils/feature";
import { omap } from "@/utils/object-utils";
import { fqnToName } from "@/utils/resolver-utils";
import { formatTwoDigit, prettyFormatBytes, titleCase } from "@/utils/string-utils";

export function getPodsForWorkloadManager(
  pods: KubernetesPodData[],
  workloadManager: KubernetesDeployment | KubernetesStatefulSet | KubernetesJob,
): KubernetesPodData[] {
  return pods.filter((pod) => {
    let ownerRefBelongs = false;
    if (workloadManager.$typeName === "chalk.kubernetes.v1.KubernetesDeployment") {
      ownerRefBelongs =
        workloadManager.replicaSets.length > 0 &&
        !!pod.ownerReference &&
        workloadManager.replicaSets.some((e) => e.uid === pod.ownerReference!.uid);
    } else {
      ownerRefBelongs = !!pod.ownerReference && pod.ownerReference.uid === workloadManager.uid;
    }
    return workloadManager.namespace === pod.namespace && ownerRefBelongs;
  });
}

export function formatAge(creationTimestamp: bigint): string {
  const now = Date.now();
  const created = Number(creationTimestamp);
  const ageMs = now - created;
  const ageDays = Math.floor(ageMs / (1000 * 60 * 60 * 24));
  if (ageDays > 0) return `${ageDays}d`;
  const ageHours = Math.floor(ageMs / (1000 * 60 * 60));
  if (ageHours > 0) return `${ageHours}h`;
  return `${Math.floor(ageMs / (1000 * 60))}m`;
}

export const ENVIRONMENT_LABEL = "chalk.ai/environment";
export const DEPLOYMENT_LABEL = "chalk.ai/deployment";
export const RESOURCE_GROUP_LABEL = "chalk.ai/resource-group";
/**
 * The service kind a pod belongs to. Prefer this over `KubernetesPodData.component`, which the
 * server pops out of the bare `component` label: both labels carry the same value, but only this
 * one survives in `KubernetesPodData.labels`.
 */
export const COMPONENT_LABEL = "app.kubernetes.io/component";

export enum KubeEventReason {
  OOMKilled = "OOMKilled",
  Evicted = "Evicted",
}

const DEFAULT_RESOURCE_GROUP_NAMES = ["", "default", "default_resource_group"];

export function isDefaultResourceGroupName(resourceGroupName: string) {
  return DEFAULT_RESOURCE_GROUP_NAMES.includes(resourceGroupName);
}

export function resourceGroupNamesMatch(a: string, b: string) {
  return a === b || (isDefaultResourceGroupName(a) && isDefaultResourceGroupName(b));
}

export const renderSingleContainerStatusProto = (
  container: KubernetesPodData_ContainerStatus,
): { text: string; className: string } => {
  const status = container;
  if (!status.state) {
    return { text: "Unknown", className: "bg-cds-gray-400" };
  } else if (status.state.running) {
    if (status.ready) {
      return { text: "Ready", className: "bg-cds-bright-green-500" };
    } else {
      return { text: "Not Ready", className: "bg-yellow-500" };
    }
  } else if (status.state.waiting) {
    return {
      text: status.state.waiting.reason || "Waiting",
      className:
        status.state.waiting.reason === "CrashLoopBackOff" ||
        status.state.waiting.reason === "ImagePullBackOff" ||
        status.state.waiting.reason === "ErrImagePull" ||
        status.state.waiting.reason === "CreateContainerConfigError"
          ? "bg-red-500"
          : "bg-yellow-500",
    };
  } else if (status.state.terminated) {
    return {
      text: status.state.terminated.reason || "Terminated",
      className:
        status.state.terminated.reason === "Completed" ? "bg-cds-bright-green-500" : "bg-red-500",
    };
  } else {
    return { text: "Unknown", className: "bg-cds-gray-400" };
  }
};

export const renderKubeFirstContainerStatusProto = (
  pod: KubernetesPodData,
): { text: string; className: string } => {
  if (pod.status == null) return { text: "Unknown", className: "bg-cds-gray-400" };
  if (pod.status.containerStatuses == null || pod.status.containerStatuses.length === 0)
    return { text: "None", className: "bg-cds-gray-400" };

  return renderSingleContainerStatusProto(pod.status.containerStatuses[0]);
};

export const getEnvVarValue = (envVar: KubernetesPodData_EnvVar): string => {
  if (envVar.valueFrom?.fieldRef) {
    const { apiVersion, fieldPath } = envVar.valueFrom.fieldRef;
    return `(${apiVersion ? apiVersion + ":" : ""}${fieldPath})`;
  }
  if (envVar.valueFrom?.resourceFieldRef) {
    const { resource } = envVar.valueFrom.resourceFieldRef;
    return `(${resource})`;
  }
  if (envVar.valueFrom?.configMapKeyRef) {
    const { key, name, optional } = envVar.valueFrom.configMapKeyRef;
    return `<set to the key '${key}' of config map '${name}'> (optional: ${optional})`;
  }
  if (envVar.valueFrom?.secretKeyRef) {
    const { key, name, optional } = envVar.valueFrom.secretKeyRef;
    return `<set to the key '${key}' of secret '${name}'> (optional: ${optional})`;
  }
  return envVar.value ?? "";
};

export function kubePodPrettyStatusText(pod: KubernetesPodData) {
  if (pod.status == null) return "Unknown";
  if (
    pod.deletionTimestamp > 0 && // "zero" timestamp bigint is -<largenumber>
    pod.status.phase !== "Failed" &&
    pod.status.phase !== "Succeeded"
  ) {
    return "Terminating";
  }
  return pod.status.phase ?? "Unknown";
}

export interface KubePodStatusBlob {
  status: string;
  totalContainers: number;
  readyContainers: number;
  colorClassName: string;
}

const isMemoryPressureEviction = (pod: KubernetesPodData): boolean => {
  const status = pod.status;
  if (status?.reason !== KubeEventReason.Evicted) {
    return false;
  }

  const evictionMessages = [
    status.message,
    ...(status.conditions ?? []).map((condition) => condition.message),
  ];
  return evictionMessages.some((message) => {
    const messageLower = message?.toLowerCase();
    return (
      messageLower?.includes("low on resource: memory") ||
      messageLower?.includes("has larger consumption of memory")
    );
  });
};

/**
 * Return a detailed status blob that can be used to render the full status of a pod in a table.
 * This should roughly mirror the "ready" and "status" columns in `kubectl get pods`.
 */
export function kubePodStatusFull(pod: KubernetesPodData): KubePodStatusBlob {
  let totalContainers = pod.spec?.containers?.length ?? 0;
  let readyContainers = 0;

  const podPhase = pod.status?.phase ?? "Unknown";
  let reason = pod.status?.reason ?? podPhase;

  // If the Pod carries {type:PodScheduled, reason:SchedulingGated}, set reason to 'SchedulingGated'.
  for (const condition of pod.status?.conditions ?? []) {
    if (condition.type === "PodScheduled" && condition.reason === "SchedulingGated") {
      reason = "SchedulingGated";
    }
  }

  // Build map of init containers to check if they're restartable
  const initContainers = new Map<string, KubernetesPodData_Container>();
  for (const initContainer of pod.spec?.initContainers ?? []) {
    initContainers.set(initContainer.name, initContainer);
    // Restartable init containers contribute to total count
    if (initContainer.restartPolicy === "Always") {
      totalContainers++;
    }
  }

  // Process init container statuses
  let initializing = false;
  for (let i = 0; i < (pod.status?.initContainerStatuses?.length ?? 0); i++) {
    const container = pod.status!.initContainerStatuses![i];
    const initContainerSpec = initContainers.get(container.name);
    const isRestartable = initContainerSpec?.restartPolicy === "Always";

    // Successfully completed init container (exit code 0) - continue to next container
    if (container.state?.terminated?.exitCode === 0) {
      continue;
    }

    // Restartable init container that's started - continue to next container
    if (isRestartable && container.started) {
      if (container.ready) {
        readyContainers++;
      }
      continue;
    }

    // If we reach here, we have an init container that's blocking - set initializing and break

    // Failed init container (terminated)
    if (container.state?.terminated) {
      if (container.state.terminated.reason) {
        reason = "Init:" + container.state.terminated.reason;
      } else if (container.state.terminated.signal !== 0) {
        reason = `Init:Signal:${container.state.terminated.signal}`;
      } else {
        reason = `Init:ExitCode:${container.state.terminated.exitCode}`;
      }
      initializing = true;
    }
    // Waiting init container with reason
    else if (
      container.state?.waiting?.reason &&
      container.state.waiting.reason !== "PodInitializing"
    ) {
      reason = "Init:" + container.state.waiting.reason;
      initializing = true;
    }
    // Init container still in progress
    else {
      reason = `Init:${i}/${pod.spec?.initContainers?.length ?? 0}`;
      initializing = true;
    }

    // Break after setting initializing (matching Go's break at end of switch)
    break;
  }

  // Process regular containers if not initializing or if pod is initialized
  const isPodInitialized = pod.status?.conditions?.some(
    (c) => c.type === "Initialized" && c.status === "True",
  );
  if (!initializing || isPodInitialized) {
    let hasRunning = false;
    let errorReason = "";

    // Process container statuses in reverse order
    const containerStatuses = pod.status?.containerStatuses ?? [];
    for (let i = containerStatuses.length - 1; i >= 0; i--) {
      const container = containerStatuses[i];

      // Waiting state
      if (container.state?.waiting?.reason) {
        reason = container.state.waiting.reason;
      }
      // Terminated state
      else if (container.state?.terminated) {
        if (container.state.terminated.reason) {
          reason = container.state.terminated.reason;
        } else if (container.state.terminated.signal !== 0) {
          reason = `Signal:${container.state.terminated.signal}`;
        } else {
          reason = `ExitCode:${container.state.terminated.exitCode}`;
        }
        if (container.state.terminated.exitCode !== 0) {
          errorReason = reason;
        }
      }
      // Ready and running
      else if (container.ready && container.state?.running) {
        hasRunning = true;
        readyContainers++;
      }
    }

    // Change pod status back to "Running" if there is at least one container still reporting as "Running" status
    if (reason === "Completed") {
      const hasPodReady = pod.status?.conditions?.some(
        (c) => c.type === "Ready" && c.status === "True",
      );
      if (hasRunning && hasPodReady) {
        reason = "Running";
      } else if (errorReason) {
        reason = errorReason;
      } else if (hasRunning) {
        reason = "NotReady";
      }
    }
  }

  // Handle deletion and unreachable states
  if (
    pod.deletionTimestamp &&
    pod.deletionTimestamp > 0 && // "zero" timestamp bigint is negative
    pod.status?.reason === "NodeLost"
  ) {
    reason = "Unknown";
  } else if (
    pod.deletionTimestamp &&
    pod.deletionTimestamp > 0 &&
    pod.status?.phase !== "Failed" &&
    pod.status?.phase !== "Succeeded"
  ) {
    reason = "Terminating";
  }

  // Kubernetes records some node memory-pressure evictions as Evicted pods whose
  // container state only says Error or ContainerStatusUnknown.
  if (isMemoryPressureEviction(pod)) {
    reason = KubeEventReason.OOMKilled;
  }

  // Determine color class based on status
  let colorClassName = "bg-cds-gray-400"; // default

  const statusLower = reason.toLowerCase();

  // Red background for error states
  if (
    statusLower.includes("crashloopbackoff") ||
    statusLower.includes("imagepullbackoff") ||
    statusLower.includes("errimagepull") ||
    statusLower.includes("createcontainerconfigerror") ||
    statusLower.includes("oomkilled") ||
    statusLower === "error" ||
    statusLower === "failed"
  ) {
    colorClassName = "bg-red-500";
  }
  // Purple for terminating
  else if (statusLower === "terminating") {
    colorClassName = "bg-purple-500";
  }
  // Green for successful states
  else if (
    statusLower === "running" ||
    statusLower === "succeeded" ||
    statusLower === "completed"
  ) {
    if (statusLower === "running") {
      colorClassName = kubePodPrettyStatusColorClassName(pod);
    } else {
      colorClassName = "bg-cds-bright-green-500";
    }
  }
  // Check for terminated state - red unless completed
  else if (statusLower.includes("terminated")) {
    if (statusLower.includes("completed")) {
      colorClassName = "bg-cds-bright-green-500";
    } else {
      colorClassName = "bg-red-500";
    }
  }
  // Check for init container errors (Init:CrashLoopBackOff, etc.)
  else if (statusLower.startsWith("init:")) {
    const initReason = statusLower.substring(5);
    if (
      initReason.includes("crashloopbackoff") ||
      initReason.includes("imagepullbackoff") ||
      initReason.includes("errimagepull") ||
      initReason.includes("oomkilled") ||
      initReason.includes("error") ||
      initReason.startsWith("exitcode:") ||
      initReason.startsWith("signal:")
    ) {
      colorClassName = "bg-red-500";
    } else {
      colorClassName = "bg-yellow-500"; // Init in progress
    }
  }
  // Yellow for pending/waiting states
  else if (
    statusLower === "pending" ||
    statusLower === "containercreating" ||
    statusLower === "podscheduling" ||
    statusLower === "schedulinggated" ||
    statusLower === "notready" ||
    statusLower === "podinitializing"
  ) {
    colorClassName = "bg-yellow-500";
  }

  return {
    status: reason,
    totalContainers,
    readyContainers,
    colorClassName,
  };
}

export function kubePodPrettyStatusColorClassName(pod: KubernetesPodData) {
  const statusText = kubePodPrettyStatusText(pod);
  const firstContainerStatus = renderKubeFirstContainerStatusProto(pod);
  switch (statusText) {
    case "Running":
      if (firstContainerStatus.text !== "Ready") return "bg-yellow-500";
      return "bg-cds-bright-green-500";
    case "Succeeded":
      return "bg-cds-bright-green-500";
    case "Pending":
      if (firstContainerStatus.text === "CreateContainerConfigError") return "bg-red-500";
      return "bg-yellow-500";
    case "Failed":
      return "bg-red-500";
    case "Terminating":
      return "bg-purple-500";
    case "Unknown":
    default:
      return "bg-cds-gray-400";
  }
}

export const podStatusToClassNameProto = (
  status: KubernetesPodData_PodStatus | undefined,
  runningPending?: boolean,
) => {
  if (status == null) {
    return "bg-cds-gray-400";
  }
  switch (status.phase) {
    case "Running":
      if (runningPending) {
        return "bg-yellow-500";
      }
      return "bg-cds-bright-green-500";
    case "Succeeded":
      return "bg-cds-bright-green-500";
    case "Pending":
      return "bg-yellow-500";
    case "Failed":
      return "bg-red-500";
    case "Unknown":
    default:
      return "bg-cds-gray-400";
  }
};

export function getPodsContributingToRequests(pods: KubernetesPodData[]) {
  return pods.filter((pod) => {
    return pod.status?.phase === "Running" || pod.status?.phase === "Pending";
  });
}

/** A pod managed by a DaemonSet (one replica per node, e.g. kube-proxy, ddog). */
export function isDaemonSetPod(pod: KubernetesPodData): boolean {
  return pod.ownerReference?.kind === "DaemonSet";
}

/**
 * A terminated pod that has run to completion. These are typically finished
 * Jobs (telemetry migrations) that clutter the node view; Failed pods are
 * intentionally excluded so crashes stay visible.
 */
export function isCompletedPod(pod: KubernetesPodData): boolean {
  return pod.status?.phase === "Succeeded";
}

/**
 * Format a CPU quantity (in cores) the way the Kubernetes resources form does:
 * sub-core values render as millicores ("490m"), whole/larger values as cores
 * ("3.49").
 */
export function formatCpuCores(cores: number): string {
  if (cores > 0 && cores < 1) {
    return `${Math.round(cores * 1000)}m`;
  }
  return formatTwoDigit(cores);
}

export function getTotalCPURequestContainers(containers: KubernetesPodData_Container[]) {
  const totalNumeric = containers.reduce((acc, container) => {
    const cpu = container.resources?.requests?.cpu?.string;
    return cpu != null ? parseCPURequest(cpu) + acc : acc;
  }, 0);

  return totalNumeric;
}

export function getTotalMemoryRequestContainers(containers: KubernetesPodData_Container[]) {
  const totalNumeric = containers.reduce((acc, container) => {
    const memory = container.resources?.requests?.memory?.string;
    return memory != null ? parseMemoryRequest(memory) + acc : acc;
  }, 0);

  return totalNumeric;
}

export function getTotalEphemeralStorageRequestContainers(
  containers: KubernetesPodData_Container[],
) {
  const totalNumeric = containers.reduce((acc, container) => {
    // Ephemeral storage lives under the generic resource map, not a named field.
    const storage = container.resources?.requests?.["ephemeral-storage"]?.string;
    return storage != null ? parseKubeMemoryBytes(storage) + acc : acc;
  }, 0);

  return totalNumeric;
}

export function getTotalCPURequest(pods: KubernetesPodData[]) {
  const totalNumeric = pods.reduce((acc, pod) => {
    if (pod.spec == null) return acc;
    const forPod = getTotalCPURequestContainers(pod.spec.containers);
    return forPod + acc;
  }, 0);

  return totalNumeric / 1000;
}

export function getTotalMemoryRequest(pods: KubernetesPodData[]) {
  const totalNumeric = pods.reduce((acc, pod) => {
    if (pod.spec == null) return acc;
    const forPod = getTotalMemoryRequestContainers(pod.spec.containers);
    return forPod + acc;
  }, 0);

  return totalNumeric;
}

export function reformatCPUAmountKube(cpu: string): string {
  const numeric = parseCPURequest(cpu);
  return formatTwoDigit(numeric / 1000);
}

export function parseCPURequest(cpu: string): number {
  if (cpu.endsWith("m")) {
    return parseInt(cpu.slice(0, -1), 10);
  } else {
    return parseInt(cpu, 10) * 1000;
  }
}

export function reformatMemoryAmountKube(memory: string): string {
  return prettyFormatBytes(parseMemoryRequest(memory));
}

/**
 * Format a Kubernetes memory/storage quantity for display. Accepts either a
 * suffixed quantity ("389808280Ki") or a plain byte count ("268435456000").
 * Returns the input unchanged when it cannot be parsed.
 */
export function prettyFormatKubeMemory(memory: string): string {
  const bytes = parseKubeMemoryBytes(memory);
  return bytes > 0 ? prettyFormatBytes(bytes, { binary: true }) : memory;
}

/**
 * Parse a Kubernetes memory/storage quantity to bytes, accepting either a
 * suffixed quantity ("389808280Ki") or a plain byte count ("268435456000").
 * Returns 0 when it cannot be parsed.
 */
export function parseKubeMemoryBytes(memory: string): number {
  const trimmed = memory.trim();
  return /^\d+$/.test(trimmed) ? Number(trimmed) : parseMemoryRequest(trimmed);
}

export const memory_units = {
  K: 1000,
  M: 1000 * 1000,
  G: 1000 * 1000 * 1000,
  T: 1000 * 1000 * 1000 * 1000,
  P: 1000 * 1000 * 1000 * 1000 * 1000,
  Ki: 1024,
  Mi: 1024 * 1024,
  Gi: 1024 * 1024 * 1024,
  Ti: 1024 * 1024 * 1024 * 1024,
  Pi: 1024 * 1024 * 1024 * 1024 * 1024,
};

// Parses the number of bytes in a memory specification string
export const parseMemoryRequest = (memory: string): number => {
  const regex = /^(\d+)([KMGTP]i?)$/;
  const match = regex.exec(memory);
  if (match) {
    const value = parseInt(match[1], 10);
    const unit = match[2] as keyof typeof memory_units;
    if (memory_units[unit]) {
      return value * memory_units[unit];
    }
  }

  return 0;
};

const COMPONENT_ORDER = [
  "engine",
  "engine-grpc",
  "branch",
  "job",
  "offline-query-consumer",
  "job-queue-manager",
  "offline-query-worker",
  "offline-query",
  "background-persistence",
  "api-server",
  "go-api-server",
  "chalk-frontend",
  "streaming",
  "streaming-worker",
  "sql",
  "engine-shadow",
  "workflow",
  "system",
];

export const KUBE_COMPONENT_ORDER_MAP = Object.fromEntries(COMPONENT_ORDER.map((c, i) => [c, i]));

export const resourceGroupFromPod = (pod: KubernetesPodData): string | undefined => {
  const rawResourceGroup = pod.labels?.["chalk.ai/resource-group"];
  return rawResourceGroup === "default_resource_group" ? "default" : rawResourceGroup;
};

/**
 * Keeps only the pods that one service kind of one resource group in one environment owns.
 *
 * `GetNodesAndPodsUI`'s `environment_id` picks which *clusters* to list, not which pods, so a
 * response can carry sibling environments that share a cluster; the environment label is what
 * narrows it to one, as the Kubernetes pane's "only environment pods" toggle does.
 *
 * All three labels must be present, mirroring a Kubernetes equality selector, which never matches a
 * pod missing the key. That is not a stricter filter than the pane's in practice: the builder
 * stamps all three in one place (`podMetadataFromDeploymentContext`), so a pod carrying the
 * component and resource-group labels always carries the environment label too. The resource group
 * goes through `resourceGroupNamesMatch` because callers spell the default group `default` while
 * the cluster labels it `default_resource_group`.
 */
export const filterPodsForResourceGroupComponent = (
  pods: KubernetesPodData[],
  {
    component,
    environmentId,
    resourceGroupName,
  }: { component: string; environmentId: string; resourceGroupName: string },
): KubernetesPodData[] =>
  pods.filter((pod) => {
    const podResourceGroup = pod.labels?.[RESOURCE_GROUP_LABEL];
    return (
      pod.labels?.[COMPONENT_LABEL] === component &&
      pod.labels?.[ENVIRONMENT_LABEL] === environmentId &&
      podResourceGroup != null &&
      resourceGroupNamesMatch(podResourceGroup, resourceGroupName)
    );
  });

export const resolverRunFromPod = (pod: KubernetesPodData) => {
  if (pod.component === "job") {
    if (pod.name.startsWith("kj-")) {
      const requestEnv = pod.spec?.containers[0]?.env?.find(
        (e) => e.name === "EXECUTE_RESOLVER_REQUEST",
      );
      if (requestEnv) {
        const request = JSON.parse(requestEnv.value);
        return omap(request["fqn"], fqnToName);
      }
    } else if (pod.name.startsWith("kqj-")) {
      const requestEnv = pod.spec?.containers[0]?.env?.find(
        (e) => e.name === "EXECUTE_OFFLINE_QUERY_REQUEST",
      );
      if (requestEnv) {
        const request = JSON.parse(requestEnv.value);
        return omap(request["cron_name"], fqnToName);
      }
    } else if (pod.name.startsWith("kab-")) {
      const requestEnv = pod.spec?.containers[0]?.env?.find(
        (e) => e.name === "EXECUTE_AGGREGATION_BACKFILL_REQUEST",
      );
      if (requestEnv) {
        const request = JSON.parse(requestEnv.value);
        const featuresList = request["features"];
        if (featuresList == null || featuresList.length === 0) {
          return "Aggregation Backfill";
        }
        const firstFeature = transformWindowedFeatureName(featuresList[0]);
        const nameString =
          featuresList.length > 1
            ? `${firstFeature} + ${featuresList.length - 1} more`
            : firstFeature;
        return `Backfill: ${nameString}`;
      } else {
        return "Aggregation Backfill";
      }
    }
  }
  return undefined;
};

export const jobIdFromPod = (pod: KubernetesPodData): string | undefined => {
  return pod.labels?.["job-name"];
};

export const offlineQueryDataFromPod = (pod: KubernetesPodData): string | undefined => {
  return pod.labels?.["offline_query_id"];
};

export function titleForKubeComponent(component: string | undefined) {
  switch (component) {
    case "api-server":
      return "API server (Python)";
    case "go-api-server":
      return "API server (Go)";
    case "chalk-frontend":
      return "Frontend server";
    case "branch":
      return "Branch server";
    case "engine":
      return "Query server (HTTP)";
    case "engine-grpc":
      return "Query server (gRPC)";
    case "job":
      return "Scheduled resolver and query runs";
    case "background-persistence":
      return "Background persistence writers";
    case "offline-query-worker":
    case "offline-query":
      return "Async offline query runs";
    case "streaming":
    case "streaming-worker":
      return "Streaming Server";
    case "sql":
      return "SQL Server";
    case "engine-shadow":
      return "Engine (shadow)";
    case "workflow":
      return "Argo Workflow Pods";
    case "metrics-db":
      return "Metrics DB";
    case "envoy-gateway":
      return "Envoy Gateway";
    case undefined:
    case "":
    case "system":
      return "Kube System";
    default:
      return titleCase(component.replaceAll("-", " "));
  }
}

export const componentsRunningPython = new Set([
  "background-persistence",
  "engine",
  "engine-grpc",
  "branch",
  "job",
  "offline-query",
  "offline-query-consumer",
  "udf-invoker",
]);

// A pod's container can have a stack trace if:
//  - the pod is running
//  - the pod is running a python process
// TODO: the image check should be better — it checks for an image expected to run python.
export function containerHasStackTrace(
  pod: KubernetesPodData,
  container: KubernetesPodData_ContainerStatus,
): boolean {
  if (pod.status?.phase !== "Running") return false;
  if (!componentsRunningPython.has(pod.component)) return false;
  return (
    !!container.image &&
    container.image.includes("engine") &&
    !container.image.includes("engine-proxy") &&
    !container.image.includes("go")
  );
}

export function findStackTraceContainer(
  pod: KubernetesPodData,
): KubernetesPodData_ContainerStatus | undefined {
  return (pod.status?.containerStatuses ?? []).find(
    (c) =>
      !!c.image &&
      c.image.includes("engine") &&
      !c.image.includes("engine-proxy") &&
      !c.image.includes("go"),
  );
}

/** Label used to group OOM-killed pods in the deployment and sidebar warnings. */
export const OOM_KILLED_BAD_STATE = KubeEventReason.OOMKilled;

const isOomTermination = (state: KubernetesPodData_ContainerState | undefined): boolean =>
  state?.terminated?.reason === KubeEventReason.OOMKilled;

/**
 * Waiting reasons that mean "this container just failed and is waiting to run again" without
 * naming why it failed. Two things follow, and both uses below rely on exactly this property:
 * the previous termination is the only record of the cause, so `lastState` is trustworthy
 * evidence of the *current* failure; and an OOM kill found there describes the pod better than
 * the reason itself. Reasons that do name a cause (image pull, config) are excluded from both.
 */
const UNEXPLAINED_RESTART_WAITING_REASONS = new Set(["CrashLoopBackOff", "Error"]);

/**
 * Whether a container is down for memory reasons *right now*.
 *
 * `lastState` is consulted only while the container waits to restart. A container whose current
 * state is a termination is judged on that termination alone, because `terminated{Completed}` is
 * the normal, permanent success state of a plain init container: a healthy pod whose init
 * container OOMed once and succeeded on retry keeps `lastState.terminated{OOMKilled}` for the
 * rest of its life, and must not read as an error. The same shape occurs for Job pods that
 * succeeded after an OOM-killed attempt.
 */
const isCurrentlyOoming = (status: KubernetesPodData_ContainerStatus): boolean => {
  if (isOomTermination(status.state)) return true;

  const waitingReason = status.state?.waiting?.reason;
  return (
    waitingReason != null &&
    UNEXPLAINED_RESTART_WAITING_REASONS.has(waitingReason) &&
    isOomTermination(status.lastState)
  );
};

/**
 * OOM detection shared by the pod table's display status and the deployment/sidebar
 * warnings so the two cannot disagree about which pods are OOMing. Covers the three
 * shapes Kubernetes uses for a pod that is down right now:
 *  - a container terminated with reason `OOMKilled`
 *  - a container waiting out its restart backoff, which reports only `CrashLoopBackOff` in
 *    `state` and names the kill in `lastState`
 *  - a node memory-pressure eviction, where the container state says nothing more than
 *    `Error` or `ContainerStatusUnknown` (see `isMemoryPressureEviction`)
 *
 * Init containers are included so an init container OOMing (`Init:OOMKilled`, or a crashloop
 * during init) is caught, but see `isCurrentlyOoming` for why a *completed* one is not.
 */
export function getPodOomBadState(pod: KubernetesPodData): string | undefined {
  if (isMemoryPressureEviction(pod)) return OOM_KILLED_BAD_STATE;

  return [
    ...(pod.status?.containerStatuses ?? []),
    ...(pod.status?.initContainerStatuses ?? []),
  ].some(isCurrentlyOoming)
    ? OOM_KILLED_BAD_STATE
    : undefined;
}

const BAD_WAITING_REASONS = new Set([
  "CrashLoopBackOff",
  "ErrImagePull",
  "ImagePullBackOff",
  "CreateContainerConfigError",
  "Error",
]);

export function getPodBadState(pod: KubernetesPodData): string | undefined {
  const waitingReason = pod.status?.containerStatuses?.find((c) =>
    BAD_WAITING_REASONS.has(c.state?.waiting?.reason ?? ""),
  )?.state?.waiting?.reason;

  // A confirmed OOM kill replaces a waiting reason that names no cause, and never one that does
  // (image pull, config) — see UNEXPLAINED_RESTART_WAITING_REASONS.
  const oomBadState = getPodOomBadState(pod);
  if (
    oomBadState != null &&
    (waitingReason == null || UNEXPLAINED_RESTART_WAITING_REASONS.has(waitingReason))
  ) {
    return oomBadState;
  }
  return waitingReason;
}

export function getBadPods(pods: KubernetesPodData[]): Array<[KubernetesPodData, string]> {
  return pods
    .map((pod): [KubernetesPodData, string | undefined] => [pod, getPodBadState(pod)])
    .filter((tup): tup is [KubernetesPodData, string] => tup[1] != null);
}

/**
 * Sums the total CPU capacity across multiple nodes.
 * Returns the total in millicores.
 */
export function sumNodesCPU(
  nodes: Array<{ totalCpu?: string; allocatableCpu?: string }>,
  options: { useAllocatable?: boolean } = {},
): number {
  const { useAllocatable = false } = options;
  return nodes.reduce((acc, node) => {
    const cpu = useAllocatable ? node.allocatableCpu : node.totalCpu;
    return cpu != null ? parseCPURequest(cpu) + acc : acc;
  }, 0);
}

/**
 * Sums the total memory capacity across multiple nodes.
 * Returns the total in bytes.
 */
export function sumNodesMemory(
  nodes: Array<{ totalMemory?: string; allocatableMemory?: string }>,
  options: { useAllocatable?: boolean } = {},
): number {
  const { useAllocatable = false } = options;
  return nodes.reduce((acc, node) => {
    const memory = useAllocatable ? node.allocatableMemory : node.totalMemory;
    return memory != null ? parseMemoryRequest(memory) + acc : acc;
  }, 0);
}

export interface KubeNodeStatusBlob {
  status: string;
  colorClassName: string;
}

/**
 * Return a detailed status blob for a Kubernetes node.
 * This mirrors the "status" column in `kubectl get nodes`.
 */
export function kubeNodeStatusFull(node: KubernetesNodeData): KubeNodeStatusBlob {
  const conditionMap = new Map<string, { status: string; type: string }>();

  // Build a map of conditions
  for (const cond of node.status?.conditions ?? []) {
    conditionMap.set(cond.type, { status: cond.status, type: cond.type });
  }

  const statuses: string[] = [];

  // Check Ready condition
  const readyCondition = conditionMap.get("Ready");
  if (readyCondition) {
    if (readyCondition.status === "True") {
      statuses.push("Ready");
    } else {
      statuses.push("NotReady");
    }
  }

  // If no conditions found, mark as Unknown
  if (statuses.length === 0) {
    statuses.push("Unknown");
  }

  // Check if node is unschedulable
  if (node.spec?.unschedulable || node.unschedulable) {
    statuses.push("SchedulingDisabled");
  }

  const status = statuses.join(",");
  const statusLower = status.toLowerCase();

  // Determine color class
  let colorClassName = "bg-cds-gray-400"; // default

  if (statusLower.includes("ready") && !statusLower.includes("notready")) {
    // Ready (possibly with SchedulingDisabled)
    if (statusLower.includes("schedulingdisabled")) {
      colorClassName = "bg-yellow-500";
    } else {
      colorClassName = "bg-cds-bright-green-500";
    }
  } else if (statusLower.includes("notready")) {
    // NotReady
    colorClassName = "bg-red-500";
  } else if (statusLower === "unknown") {
    // Unknown
    colorClassName = "bg-cds-gray-400";
  }

  return {
    status,
    colorClassName,
  };
}
